import { AiResultContainer, FCX, getRem } from "@datassential/platform-ui-lib";
import AiResultQuestion from "@vms/AiResult/AiResultQuestion";
import { ComponentProps, ReactNode, useMemo } from "react";
import AiResultFeedback from "@vms/AiResult/AiResultFeedback";
import AiResultSettings, { AiResultSettingsProps, SettingsItem } from "@vms/AiResult/AiResultSettings";
import cn from "classnames";
import Skeleton from "@components/Skeleton";

const AiResult: FCX<{
    question: ReactNode;
    answer: ReactNode;
    settings: AiResultSettingsProps["settings"];
    isAnswerLoading?: boolean;
    isSettingsLoading?: boolean;
}> = ({
    question,
    answer: rawAnswer,
    settings: rawSettings,
    className,
    isAnswerLoading = false,
    isSettingsLoading = false,
}) => {
    const settingsSkeleton = useMemo(
        () => {
            const titleSkeleton = <Skeleton width={120} height={20}/>;
            const valueSkeleton = <Skeleton width={160} height={20} style={{ marginTop: getRem(6) }}/>;
            return Array(4).fill(null).map(() => ({
                title: titleSkeleton,
                value: valueSkeleton,
                isLoader: true,
            }));
        },
        []
    );

    const settings = useMemo(
        () => {
            if (isSettingsLoading) return settingsSkeleton;

            return rawSettings;
        },
        [isSettingsLoading, settingsSkeleton, rawSettings]
    );

    const answerSkeleton = useMemo(
        () => {
            return (
                <>
                    <Skeleton height={44}/>
                    <Skeleton height={44}/>
                </>
            );
        },
        []
    );

    const answer = useMemo(
        () => {
            if (isAnswerLoading) {
                return answerSkeleton;
            }

            return rawAnswer;
        },
        [isAnswerLoading, answerSkeleton, rawAnswer]
    );

    const resultFeedback = useMemo(
        () => {
            if (isAnswerLoading) {
                return <Skeleton width={200} height={40}/>;
            }

            return <AiResultFeedback className="appears"/>;
        },
        [isAnswerLoading]
    );

    return (
        <AiResultContainer className={cn("AiResult", className)}>
            <div className="AiResult__result-container">
                <AiResultQuestion className="appears" question={question}/>
                <div className="AiResult__result">
                    {answer}
                </div>
                {resultFeedback}
            </div>
            <AiResultSettings settings={settings}/>
        </AiResultContainer>
    );
};

export type AiResultProps = ComponentProps<typeof AiResult>;

export default AiResult;